import { useDispatch } from "react-redux";
import { useState, useRef, useEffect } from "react";
import Checkbox from "antd/lib/checkbox/Checkbox";
import TextArea from "antd/lib/input/TextArea";
import { Button, Col, Input, message, Row, Select } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";

import { useTypedSelector } from "@hooks";
import { types } from "./config";
import {
  ErrorServices,
  useCreateOfferDetailsMutation,
  useFetchAllBusinessUnitsQuery,
  useFetchAllCompanyJobGradesQuery,
  useFetchAllReportingGradesQuery,
  // useFetchAllJobFunctionsQuery,
  useFetchAllCompanyJobFunctionsQuery,
  useFetchCurrenciesQuery,
  useUpdateOfferDetailsMutation,
  useFetchCompanyCountriesQuery,
  useFetchJobGradesByCountryQuery,
  useFetchRangeTypeQuery,
} from "@services";
import {
  handlePositionDetails,
  handleStage,
  resetOfferModeller,
  resetOffers,
} from "@store/offers";
import {
  IBusinessUnitItem,
  ICountry,
  ICurrency,
  IJobFunctionItem,
  IJobGrade,
  IRegion,
  ISubJobFunctionItem,
} from "@types";
import CustomButton from "@/components/customButton";
import CollapsibleCard from "@/components/CollapsibleCard";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";

const { Option } = Select;

const OfferDetails = ({
  offerModeller,
  onResponse,
  detailedView,
}: {
  offerModeller?: boolean;
  onResponse: any;
  detailedView: any;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const draft = useRef<boolean>(false);
  const is_create = window.location.pathname.includes("/create");
  const { offer } = useTypedSelector(state => state?.offers);
  const [customView, setCustomView] = useState(detailedView);
  const { search } = useLocation();
  const isRevised = search === "?mode=revise";
  const readOnly =
    offerModeller || (offer && offer?.status !== "DRAFTED" && !isRevised)
      ? true
      : false;
  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const { positionDetails } = useTypedSelector(state => state?.offers);
  const {
    grade,
    job_title,
    type,
    region,
    country_id,
    company_job_function,
    company_sub_job_function_id,
    reporting_grade,
    position_owner,
    city,
    grade_type,
    reporting_title,
    cost_centre,
    is_critical,
    position_comment,
    business_unit,
    currency_id,
    range_type,
  } = positionDetails;

  // const { data: gradesData, isLoading: isLoadingGrades } =
  //   useFetchAllCompanyJobGradesQuery({ company_id });
  const { data: reportingGradesData, isLoading: isLoadingReportingGrades } =
    useFetchAllReportingGradesQuery({ company_id });
  const { data: countriesData } = useFetchCompanyCountriesQuery({
    company_id,
  });
  const {
    data: gradesData,
    isLoading: isLoadingGrades,
    isFetching: isFetchingGrades,
  } = useFetchJobGradesByCountryQuery(
    { company_id, countries_id: country_id },
    { refetchOnMountOrArgChange: true }
  );
  const { data: companyCountries } = countriesData || {};
  const { data: grades } = gradesData || {};
  const { data: reportingGrades } = reportingGradesData || {};
  const { data: companyJobFunctionsData, isLoading: isLoadingCompanyJobFunctions } =
    useFetchAllCompanyJobFunctionsQuery({
      companyId: company_id
    });
  const { data: companyJobFunctions } = companyJobFunctionsData || {};
  const { data: businessUnitsData, isLoading: isLoadingBusinessUnits } =
    useFetchAllBusinessUnitsQuery({ company_id });
  const { data: businessUnits } = businessUnitsData || {};
  const [subJobFunctions, setSubJobFunctions] = useState<ISubJobFunctionItem[]>(
    []
  );
  const { data: currenciesData, isLoading: isFetchingCurrencies } =
    useFetchCurrenciesQuery(null);
  const { data: currencies } = currenciesData || {};
  const [regions, setRegions] = useState<Partial<IRegion>[]>([]);
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [createOfferDetails, { isLoading: isCreatingOfferDetails }] =
    useCreateOfferDetailsMutation();
  const [updateOfferDetails, { isLoading: isUpdatingOfferDetails }] =
    useUpdateOfferDetailsMutation();
  const { data: ranges, isLoadingRange } = useFetchRangeTypeQuery(
    {
      company_id: company_id,
      grade: grade,
      country_id: country_id,
    },
    {
      skip: !country_id || !grade,
      refetchOnMountOrArgChange: true,
    }
  );
  const rangesData = ranges?.data || [];
  const disabled =
    !type ||
    !job_title ||
    !grade ||
    !company_job_function ||
    // !company_sub_job_function_id ||
    !business_unit ||
    !region ||
    !country_id ||
    !currency_id;
  // !grade_type ||
  // !range_type
  useEffect(() => {
    // if (!is_create) {
    const companyJobFunction = companyJobFunctions?.find(
      (func: Partial<IJobFunctionItem>) => func.id === company_job_function?.id
    );
    const selected_grade = grades?.find(
      (x: Partial<IJobGrade>) => x.grade === grade
    );
    const bu = businessUnits?.find(
      (businessUnit: Partial<IBusinessUnitItem>) =>
        businessUnit.id === business_unit?.id
    );
    setSubJobFunctions(companyJobFunction?.sub_job_functions);
    setRegions(bu?.regions);
    const country = bu?.regions?.find((item: any) => item?.id == region?.id);
    setCountries(country?.countries.length > 0 ? country?.countries : []);
    // setCountries(
    //   selected_grade?.is_global ? companyCountries : selected_grade?.countries
    // );
    // }
  }, [
    businessUnits,
    business_unit?.id,
    companyCountries,
    // grade,
    // grades,
    is_create,
    companyJobFunctions,
    company_job_function?.id,
  ]);

  const handleChange = (event: any) => {
    if (readOnly) return;
    dispatch(
      handlePositionDetails({
        [event.target.name]: event.target.value,
      })
    );
  };

  const handleSubmit = async (is_draft: boolean) => {
    if (offer && offer.status !== "DRAFTED" && !isRevised) {
      return dispatch(handleStage("CANDIDATE_DETAILS"));
    }
    try {
      draft.current = is_draft;
      const body: any = {
        ...positionDetails,
        business_unit_id: business_unit?.id,
        company_job_function_id: company_job_function?.id,

        region_id: region?.id,
        is_draft,
      };
      delete body["business_unit"];
      delete body["company_job_function"];
      delete body["region"];
      delete body["currency"];

      if (offer?.status === "DRAFTED" || isRevised) {
        await updateOfferDetails({ company_id, id: offer?.id, body }).unwrap();
      } else {
        await createOfferDetails({ company_id, body }).unwrap();
      }

      // if (!isRevised) {
      // dispatch(resetOfferModeller());
      // }
      if (
        !isRevised &&
        positionDetails.grade !== offer?.offer_position_detail?.grade
      ) {
        dispatch(resetOfferModeller());
      }

      message.success("Offer details saved successfully");
    } catch (err) {
      ErrorServices(err);
    }
  };

  const handleUpdateClick = () => {
    onResponse(0);
    setCustomView(!customView);
  };

  const handleSaveClick = () => {
    console.log("Save button clicked");
  };

  let country_name = countries && countries?.filter(x => x.id === country_id);
  let currency_current =
    currencies && currencies.filter(x => x.id === currency_id);
  let sub_function =
    subJobFunctions &&
    subJobFunctions.filter(x => x.id === company_sub_job_function_id);
  return (
    <>
      {customView ? (
        <CollapsibleCard
          onUpdateClick={handleUpdateClick}
          onSaveClick={handleSaveClick}
          header={
            <div className="flex flex-row items-center">
              <h2 className="text-xl font-semibold">Offer Details</h2>
            </div>
          }
        >
          <h1 className="font-semibold text-lg uppercase">Offer Details</h1>
          <div className="flex flex-row justify-between mb-6">
            {/* Section 1 */}
            <div className="flex flex-col space-y-4 w-1/2">
              <div className="flex flex-row items-center mt-6">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Type
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {type ? type : "N/A"}
                </p>
              </div>
              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Company Job Function
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {company_job_function?.name ? company_job_function?.name : "N/A"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Company Sub-Job Function
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {company_sub_job_function_id
                    ? sub_function && sub_function[0]?.name
                    : "N/A"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Business Unit
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {business_unit?.name ? business_unit?.name : "N/A"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Region
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {region?.name ? region?.name : "N/A"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Current Currency
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {currency_id
                    ? currency_current && currency_current[0]?.name
                    : "N/A"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Grade
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {grade ? grade : "N/A"}
                </p>
              </div>
              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Country
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {country_id ? country_name && country_name[0]?.name : "N/A"}
                </p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="flex flex-col space-y-4 w-1/2">
              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  City
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {city ? city : "N/A"}
                </p>
              </div>
              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Job Title
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {job_title ? job_title : "N/A"}
                </p>
              </div>
              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Position Owner
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {position_owner ? position_owner : "N/A"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Job Grade Type
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {grade_type ? grade_type : "N/A"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Reporting to Grade
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {reporting_grade ? reporting_grade : "N/A"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Reporting Title
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {reporting_title ? reporting_title : "N/A"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Cost Centre
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {cost_centre ? cost_centre : "N/A"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Critical Role?
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {is_critical ? "Yes" : "No"}
                </p>
              </div>

              <div className="flex flex-row items-center">
                <h4 className="font-semibold text-sm text-themeInputTitleColor w-1/3">
                  Position Comment
                </h4>
                <p className="font-medium text-sm text-themeInputTitleColor">
                  {position_comment ? position_comment : "M/A"}
                </p>
              </div>

              {/* Add more rows for Section 2 as needed */}
            </div>
          </div>
        </CollapsibleCard>
      ) : (
        <div>
          <div>
            {/* type and job title */}
            <Row justify="space-between" className="mb-2" gutter={[24, 24]} gutter={[24, 24]}>
              {/* Type */}
              <Col span={7}>
                <label className=" leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg">
                    Type <span className="text-red-500">*</span>
                  </span>
                </label>
                <Select
                  className="custom-select"
                  size="large"
                  showArrow
                  placeholder="Select offer type from here..."
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={value =>
                    !readOnly &&
                    handleChange({ target: { name: "type", value } })
                  }
                  value={type || undefined}
                >
                  {types
                    ?.slice() // original array ko mutate na kare
                    .sort((a, b) => a.label.localeCompare(b.label)) // label alphabetically sort
                    .map(({ id, label, value }) => (
                      <Option key={id} value={value}>
                        {label}
                      </Option>
                    ))}
                </Select>
              </Col>

              {/* job title */}
              <Col span={7}>
                <div className="flex items-center justify-between w-full">
                  <label className="font-medium text-revampBlue text-lg leading-10 mt-4">
                    <span className="font-medium text-revampBlue text-lg">
                      Job Title <span className="text-red-500">*</span>
                    </span>
                  </label>
                  <Checkbox
                    checked={is_critical}
                    onChange={event =>
                      handleChange({
                        target: {
                          name: "is_critical",
                          value: event.target.checked,
                        },
                      })
                    }
                    className="offers__form__fields__checkbox mt-1"
                  >
                    <span className="font-medium text-lg text-revampBlue cursor-pointer">
                      Critical Role?
                    </span>
                  </Checkbox>
                </div>

                <Input
                  readOnly={readOnly}
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  name="job_title"
                  onChange={handleChange}
                  value={job_title || ""}
                  size="large"
                  placeholder="Enter job title here..."
                />
              </Col>

              {/* business unit */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    Business Unit <span className="text-red-500">*</span>
                  </span>
                </label>
                <Select
                  className="custom-select"
                  loading={isLoadingBusinessUnits}
                  size="large"
                  showArrow
                  placeholder="Select business unit from here..."
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={business_unit?.name || undefined}
                  onChange={(serialized_businessUnit: string) => {
                    if (readOnly) return;
                    const businessUnit = JSON.parse(serialized_businessUnit);
                    handleChange({
                      target: {
                        name: "business_unit",
                        value: {
                          id: businessUnit?.id,
                          name: businessUnit?.name,
                        },
                      },
                    });
                    dispatch(
                      handlePositionDetails({
                        region: null,
                      })
                    );
                    setRegions(businessUnit?.regions);
                  }}
                >
                  {businessUnits?.map(
                    (businessUnit: Partial<IBusinessUnitItem>) => (
                      <Option
                        key={businessUnit?.id}
                        value={JSON.stringify(businessUnit)}
                      >
                        {businessUnit?.name}
                      </Option>
                    )
                  )}
                </Select>
              </Col>
            </Row>

            {/* businet unit and region */}
            <Row justify="space-between" className="mb-2" gutter={[24, 24]} gutter={[24, 24]}>
              {/* region */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    Region <span className="text-red-500">*</span>
                  </span>
                </label>
                <Select
                  className="custom-select"
                  disabled={!business_unit?.id}
                  size="large"
                  showArrow
                  placeholder="Select region from here..."
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={region?.name || undefined}
                  onChange={(region: string) => {
                    if (readOnly) return;
                    const { name, id, countries } = JSON.parse(region);
                    handleChange({
                      target: {
                        name: "region",
                        value: { name, id },
                      },
                    });

                    if (country_id) {
                      handleChange({
                        target: { name: "country_id", value: null },
                      });
                      handleChange({ target: { name: "grade", value: null } });
                      handleChange({
                        target: { name: "range_type", value: null },
                      });
                    }

                    setCountries(countries);
                  }}
                >
                  {regions?.map((region: Partial<IRegion>) => (
                    <Option key={region?.id} value={JSON.stringify(region)}>
                      {region?.name}
                    </Option>
                  ))}
                </Select>
              </Col>

              {/*country  */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    {" "}
                    Country <span className="text-red-500">*</span>
                  </span>
                </label>
                <Select
                  className="custom-select"
                  disabled={!region}
                  size="large"
                  showArrow
                  placeholder="Select country from here..."
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={country_id || undefined}
                  onChange={value => {
                    if (readOnly) return;

                    const selectedCountry = countries?.find(
                      (country: ICountry) => country.id === value
                    );

                    handleChange({ target: { name: "country_id", value } });
                    handleChange({ target: { name: "grade", value: null } });
                    handleChange({
                      target: { name: "range_type", value: null },
                    });

                    // ✅ Auto-select currency of selected country
                    if (selectedCountry?.currency?.id) {
                      handleChange({
                        target: {
                          name: "currency_id",
                          value: selectedCountry.currency.id,
                        },
                      });
                    }
                  }}
                >
                  {countries?.map((country: ICountry) => (
                    <Option key={country?.id} value={country.id}>
                      {country?.name}
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* grade */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    {" "}
                    Grade <span className="text-red-500">*</span>
                  </span>
                </label>
                <Select
                  className="custom-select"
                  loading={isLoadingGrades}
                  size="large"
                  disabled={!country_id}
                  showArrow
                  placeholder="Select grade from here..."
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={country_id ? grade : undefined}
                  onChange={(grade: string) => {
                    !readOnly &&
                      handleChange({
                        target: {
                          name: "grade",
                          value: grade,
                        },
                      });
                    handleChange({
                      target: { name: "range_type", value: null },
                    });
                    handleChange({
                      target: { name: "reporting_grade", value: null },
                    });
                  }}
                >
                  {grades?.map((grade: Partial<IJobGrade>) => (
                    <Option key={grade?.id!} value={grade?.grade!}>
                      {grade?.grade}
                    </Option>
                  ))}
                </Select>
              </Col>


            </Row>

            {/* job function and sub-function */}
            <Row justify="space-between" className="mb-2" gutter={[24, 24]} gutter={[24, 24]}>
              {/* job function */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    {" "}
                    Company Job Function <span className="text-red-500">*</span>
                  </span>
                </label>
                <Select
                  className="custom-select"
                  loading={isLoadingCompanyJobFunctions}
                  size="large"
                  showArrow
                  placeholder="Select job function from here..."
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(serialized_jobFunction: string) => {
                    if (readOnly) return;
                    const companyJobFunction = JSON.parse(serialized_jobFunction);
                    handleChange({
                      target: {
                        name: "company_job_function",
                        value: { id: companyJobFunction?.id, name: companyJobFunction?.name },
                      },
                    });
                    dispatch(
                      handlePositionDetails({
                        company_sub_job_function_id: null,
                      })
                    );
                    setSubJobFunctions(companyJobFunction?.sub_job_functions);
                  }}
                  value={company_job_function?.name || undefined}
                >
                  {companyJobFunctions?.map(
                    (companyJobFunction: Partial<IJobFunctionItem>) => (
                      <Option
                        key={companyJobFunction?.id}
                        value={JSON.stringify(companyJobFunction)}
                      >
                        {companyJobFunction?.name}
                      </Option>
                    )
                  )}
                </Select>
              </Col>

              {/* sub function */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    Company Job Sub-Function <span className="text-red-500">*</span>
                  </span>
                </label>
                <Select
                  className="custom-select"
                  disabled={!company_job_function?.id}
                  size="large"
                  showArrow
                  placeholder="Select job sub-function from here..."
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={value =>
                    !readOnly &&
                    handleChange({
                      target: { name: "company_sub_job_function_id", value },
                    })
                  }
                  value={company_sub_job_function_id || undefined}
                >
                  {subJobFunctions
                    ?.slice()
                    ?.sort(function (a: any, b: any) {
                      if (a.name < b.name) {
                        return -1;
                      }
                      if (a.name > b.name) {
                        return 1;
                      }
                      return 0;
                    })
                    ?.map(({ id, name }: ISubJobFunctionItem) => (
                      <Option key={id} value={id}>
                        {name}
                      </Option>
                    ))}
                </Select>
              </Col>

              {/* offer currency */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    Offer Currency <span className="text-red-500">*</span>
                  </span>
                </label>
                <Select
                  className="custom-select"
                  value={currency_id || undefined}
                  loading={isFetchingCurrencies}
                  onChange={value =>
                    !readOnly &&
                    handleChange({
                      target: { name: "currency_id", value },
                    })
                  }
                  showArrow={true}
                  showSearch={true}
                  size="large"
                  filterOption={(input: any, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  placeholder="Select currency from here..."
                >
                  {currencies
                    ?.slice() // original array ko mutate na kare
                    .sort((a, b) => a.name.localeCompare(b.name)) // name ke hisaab se sort
                    .map(({ id, name, code }: ICurrency) => (
                      <Option key={id} value={id}>
                        {`${name} (${code})`}
                      </Option>
                    ))}
                </Select>
              </Col>
            </Row>

          </div>

          <div className="offers__form__fields">
            <div className="font-bold text-xl  text-center text-revampBlue my-12">
              <span className="font-semibold text-2xl text-gray-600 ">
                Other Details
              </span>
            </div>

            {/* range and job grades type */}
            <Row justify="space-between" className="mb-2" gutter={[24, 24]}>
              {/* range */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    {" "}
                    Range Name
                  </span>
                </label>
                <Select
                  className="custom-select"
                  loading={isLoadingRange}
                  size="large"
                  disabled={!country_id || !grade}
                  showArrow
                  placeholder="Select range from here..."
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={country_id || grade ? range_type : undefined}
                  onChange={(range: string) => {
                    !readOnly &&
                      handleChange({
                        target: {
                          name: "range_type",
                          value: range,
                        },
                      });
                  }}
                >
                  {rangesData?.map((range: any) => (
                    <Option key={range?.range_type!} value={range?.range_type!}>
                      {range?.range_type}
                    </Option>
                  ))}
                </Select>
              </Col>

              {/* job grade type */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    Job Grade Type
                  </span>
                </label>
                <Input
                  readOnly={readOnly}
                  size="large"
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  name="grade_type"
                  onChange={handleChange}
                  placeholder="Enter job grade type here..."
                  value={grade_type || ""}
                />
              </Col>

              {/* reporting to grade */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    Reporting to Grade
                  </span>
                </label>
                <Select
                  className="custom-select"
                  size="large"
                  showArrow
                  placeholder="Select reporting grade from here..."
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={reporting_grade || undefined}
                  onChange={value => {
                    !readOnly &&
                      handleChange({
                        target: { name: "reporting_grade", value },
                      });
                  }}
                >
                  {(reportingGrades || grades)?.map(
                    ({ id, grade }: Partial<IJobGrade>) => (
                      <Option key={id} value={grade!}>
                        {grade}
                      </Option>
                    )
                  )}
                </Select>
              </Col>
            </Row>



            {/* cost centre and postion owner */}
            <Row justify="space-between" className="mb-2" gutter={[24, 24]}>
              {/* reporting title */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    Reporting Title
                  </span>
                </label>
                <Input
                  readOnly={readOnly}
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  name="reporting_title"
                  onChange={handleChange}
                  value={reporting_title || ""}
                  size="large"
                  placeholder="Enter reporting title here..."
                />
              </Col>

              {/* cost centre */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    Cost Centre
                  </span>
                </label>
                <Input
                  readOnly={readOnly}
                  name="cost_centre"
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  onChange={handleChange}
                  value={cost_centre || ""}
                  size="large"
                  placeholder="Enter cost center here..."
                />
                {/* <br />
                <Checkbox
                  checked={is_critical}
                  onChange={event =>
                    handleChange({
                      target: {
                        name: "is_critical",
                        value: event.target.checked,
                      },
                    })
                  }
                  className="offers__form__fields__checkbox mt-4"
                >
                  <span className={"font-medium text-lg text-revampBlue"}>
                    Critical Role?
                  </span>
                </Checkbox> */}
              </Col>

              {/* position owner */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    {" "}
                    Position Owner
                  </span>
                </label>
                <Input
                  readOnly={readOnly}
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  name="position_owner"
                  onChange={handleChange}
                  value={position_owner || ""}
                  size="large"
                  placeholder="Enter position owner here..."
                />
              </Col>
            </Row>

            {/* city and critical role */}
            <Row justify="space-between" className="mb-2" gutter={[24, 24]}>
              {/* city */}
              <Col span={7}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    {" "}
                    City
                  </span>
                </label>
                <Input
                  readOnly={readOnly}
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  size="large"
                  name="city"
                  placeholder="Enter city here..."
                  onChange={handleChange}
                  value={city || undefined}
                />
              </Col>

              <Col span={24}>
                <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                  <span className="font-medium text-revampBlue text-lg ">
                    Position Comment
                  </span>
                </label>
                <TextArea
                  readOnly={readOnly}
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  name="position_comment"
                  onChange={handleChange}
                  value={position_comment || ""}
                  rows={5}
                  placeholder="Enter your comment here..."
                />
              </Col>
            </Row>



            {offerModeller ? null : (
              <div className="flex items-center fixed left-0 bottom-0 w-full  justify-around p-6  rounded-b">
                <CustomButton
                  // className="w-"
                  btnName="Go Back"
                  onClick={() => history.goBack()}
                  customClasses={`!px-10 py-3  w-2/12 ${disabled
                    ? "bg-revampDarkBlue  text-white"
                    : "bg-revampDarkBlue  text-white"
                    } font-semibold`}
                  textStyles="font-semibold"
                ></CustomButton>

                {!isRevised && checkPermission(permissions.UPDATE_OFFER) && (
                  <CustomButton
                    customClasses={`!px-10  py-3 w-2/12 font-semibold ${disabled
                      ? "bg-themeButtonGray text-themeBlack  cursor-not-allowed"
                      : "text-white bg-revampBlue"
                      }`}
                    btnName={"Save as draft"}
                    disabledProp={disabled}
                    onClick={() => handleSubmit(true)}
                  >
                    {(isCreatingOfferDetails || isUpdatingOfferDetails) &&
                      draft?.current ? (
                      <LoadingOutlined className="spinner" />
                    ) : (
                      ""
                    )}
                  </CustomButton>
                )}

                {checkPermission(permissions.UPDATE_OFFER) && (
                  <CustomButton
                    customClasses={`!px-10  py-3 w-2/12  font-semibold ${disabled
                      ? "bg-themeButtonGray text-themeBlack  cursor-not-allowed"
                      : "text-white bg-revampOrange"
                      }`}
                    btnName={"Next"}
                    disabledProp={disabled}
                    onClick={() => handleSubmit(false)}
                  >
                    {(isCreatingOfferDetails || isUpdatingOfferDetails) &&
                      !draft?.current ? (
                      <LoadingOutlined className="spinner" />
                    ) : null}
                  </CustomButton>
                )}


              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default OfferDetails;
